import { zodResolver } from "@hookform/resolvers/zod"
import axios from "axios"
import { Button } from "design-system/atoms/Button"
import { Textarea } from "design-system/atoms/Textarea"
import { Card } from "design-system/molecules/Card"
import { Form, FormControl, FormField, FormItem, FormMessage } from "design-system/organisms/Form"
import { cn } from "design-system/utils"
import { getErrorTranslations } from "lib/i18n"
import { History, LoaderCircle } from "lucide-react"
import { useCompanyQuery } from "modules/settings/hooks/useCompanyQuery"
import { useUpdateCompanyMutation } from "modules/settings/hooks/useUpdateCompanyMutation"
import { ChangeLogItem } from "modules/shared/components/ChangeLogItem"
import {
  ComposerPromptValidationIssue,
  ComposerPromptValidationIssues,
} from "modules/shared/components/ComposerPromptValidationIssues"
import { Loading } from "modules/shared/components/Loading"
import React, { useEffect, useRef, useState } from "react"
import { type Resolver, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CompanySchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"

export function AgentCustomizationForm() {
  const { t } = useTranslation()

  const { data: company, isPending } = useCompanyQuery()

  const form = useForm<z.infer<typeof CompanySchema>>({
    // Resolver validates a subset of CompanySchema fields, but the form manages the full type
    resolver: zodResolver(
      CompanySchema.pick({
        description: true,
        products: true,
        toneOfVoice: true,
        additionalInfo: true,
      }),
    ) as unknown as Resolver<z.infer<typeof CompanySchema>>,
    defaultValues: company,
  })

  const [focusedField, setFocusedField] = useState<string | null>(null)
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false)
  const [issuesByField, setIssuesByField] = useState<
    Record<string, ComposerPromptValidationIssue[]>
  >({})
  const [isSavingField, setIsSavingField] = useState<string | null>(null)
  const isValidationReviewOpenRef = useRef(false)

  useEffect(() => {
    if (company) {
      form.reset(company)
      form.trigger()
    }
  }, [company, form])

  const { mutateAsync: updateCompany } = useUpdateCompanyMutation()

  function applySuggestion(field: string, value: string) {
    if (!isCustomizationField(field)) return
    form.setValue(field, value, { shouldDirty: true })
  }

  function handleValidationReviewOpenChange(open: boolean) {
    isValidationReviewOpenRef.current = open
    if (!open) void onUpdate()
  }

  if (isPending) {
    return <Loading />
  }

  async function onUpdate(
    e?:
      | React.FocusEvent<HTMLInputElement>
      | React.FocusEvent<HTMLTextAreaElement>
      | React.FormEvent<HTMLFormElement>,
    logo?: FileList,
  ) {
    if (isValidationReviewOpenRef.current) return

    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    const valuesHaveChanged = JSON.stringify(form.getValues()) !== JSON.stringify(company)

    if (!valuesHaveChanged && !logo) {
      return
    }

    function onUndo() {
      const currentCompany = { ...company }
      form.reset(currentCompany)

      void updateCompany({
        ...currentCompany,
        logo: undefined,
      })
    }

    const currentValues = form.getValues()
    const fields = ["description", "products", "toneOfVoice", "additionalInfo"] as const
    const changedValues = fields.reduce<Partial<z.infer<typeof CompanySchema>>>((values, field) => {
      if (currentValues[field] !== company?.[field]) values[field] = currentValues[field]
      return values
    }, {})
    const changedFields = fields.filter((key) => key in changedValues)
    const target = e?.target
    const field =
      target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement
        ? target.name
        : fields.find((key) => key in changedValues)

    setIsSavingField(field ?? null)
    try {
      await updateCompany({ ...changedValues, logo: logo || undefined })
      setIssuesByField((issues) => {
        const nextIssues = { ...issues }
        for (const key of changedFields) nextIssues[key] = []
        return nextIssues
      })
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 422) {
        const issues = error.response.data?.error?.issues as
          ComposerPromptValidationIssue[] | undefined
        if (issues) {
          const groupedIssues = issues.reduce<Record<string, ComposerPromptValidationIssue[]>>(
            (grouped, issue) => {
              ;(grouped[issue.field] ??= []).push(issue)
              return grouped
            },
            {},
          )
          setIssuesByField((currentIssues) => {
            const nextIssues = { ...currentIssues }
            for (const key of changedFields) nextIssues[key] = groupedIssues[key] ?? []
            return nextIssues
          })
          return
        }
      }
      throw error
    } finally {
      setIsSavingField(null)
    }

    toast.success(t("settings.company.customization.form.updated"), {
      action: {
        label: t("shared.undo"),
        onClick: onUndo,
      },
    })
  }

  const content = (
    <div className="flex flex-col gap-2">
      <Form {...form} onSubmit={onUpdate} errorTranslations={getErrorTranslations()}>
        <div className="flex flex-col gap-4">
          <Card
            className={cn(
              "flex flex-col",
              focusedField === "description" && "border-ring ring-ring/50 ring-[3px]",
              issuesByField.description?.length &&
                "border-destructive/50 ring-1 ring-destructive/15",
            )}
          >
            <div className="flex flex-col gap-0 p-4 bg-muted rounded-t-md">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">
                  {t("settings.company.customization.form.description")}
                </h3>
                <div className="flex h-9 w-9 items-center justify-center">
                  {isSavingField === "description" ? (
                    <LoaderCircle className="h-5 w-5 animate-spin" />
                  ) : (
                    <ComposerPromptValidationIssues
                      issues={issuesByField.description ?? []}
                      getFieldText={(field) => getCustomizationFieldValue(form.getValues(), field)}
                      onApplySuggestion={applySuggestion}
                      onOpenChange={handleValidationReviewOpenChange}
                    />
                  )}
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {t("settings.company.customization.form.descriptionDescription")}
              </p>
            </div>
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ""}
                      onFocus={() => setFocusedField("description")}
                      onChange={(event) => {
                        field.onChange(event)
                      }}
                      onBlur={(e) => {
                        setFocusedField(null)
                        onUpdate(e)
                      }}
                      placeholder={t("settings.company.customization.form.descriptionDescription")}
                      rows={8}
                      className="min-h-28 p-4 text-base md:text-base relative resize-none focus-visible:ring-0 focus-visible:border-0 rounded-none border-none ml-[2px] w-[calc(100%-4px)] outline-0 overflow-hidden"
                      maxLength={1024}
                      maxLengthClassName="p-4 text-muted-foreground/70"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Card>
          <Card
            className={cn(
              "flex flex-col",
              focusedField === "products" && "border-ring ring-ring/50 ring-[3px]",
              issuesByField.products?.length && "border-destructive/50 ring-1 ring-destructive/15",
            )}
          >
            <div className="flex flex-col gap-0 p-4 bg-muted rounded-t-md">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">
                  {t("settings.company.customization.form.products")}
                </h3>
                <div className="flex h-9 w-9 items-center justify-center">
                  {isSavingField === "products" ? (
                    <LoaderCircle className="h-5 w-5 animate-spin" />
                  ) : (
                    <ComposerPromptValidationIssues
                      issues={issuesByField.products ?? []}
                      getFieldText={(field) => getCustomizationFieldValue(form.getValues(), field)}
                      onApplySuggestion={applySuggestion}
                      onOpenChange={handleValidationReviewOpenChange}
                    />
                  )}
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {t("settings.company.customization.form.productsDescription")}
              </p>
            </div>
            <FormField
              control={form.control}
              name="products"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ""}
                      onFocus={() => setFocusedField("products")}
                      onChange={(event) => {
                        field.onChange(event)
                      }}
                      onBlur={(e) => {
                        setFocusedField(null)
                        onUpdate(e)
                      }}
                      placeholder={t("settings.company.customization.form.productsDescription")}
                      rows={8}
                      className="min-h-28 p-4 text-base md:text-base relative resize-none focus-visible:ring-0 focus-visible:border-0 rounded-none border-none ml-[2px] w-[calc(100%-4px)] outline-0 overflow-hidden"
                      maxLength={1024}
                      maxLengthClassName="p-4 text-muted-foreground/70"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Card>
          <Card
            className={cn(
              "flex flex-col",
              focusedField === "toneOfVoice" && "border-ring ring-ring/50 ring-[3px]",
              issuesByField.toneOfVoice?.length &&
                "border-destructive/50 ring-1 ring-destructive/15",
            )}
          >
            <div className="flex flex-col gap-0 p-4 bg-muted rounded-t-md">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">
                  {t("settings.company.customization.form.toneOfVoice")}
                </h3>
                <div className="flex h-9 w-9 items-center justify-center">
                  {isSavingField === "toneOfVoice" ? (
                    <LoaderCircle className="h-5 w-5 animate-spin" />
                  ) : (
                    <ComposerPromptValidationIssues
                      issues={issuesByField.toneOfVoice ?? []}
                      getFieldText={(field) => getCustomizationFieldValue(form.getValues(), field)}
                      onApplySuggestion={applySuggestion}
                      onOpenChange={handleValidationReviewOpenChange}
                    />
                  )}
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {t("settings.company.customization.form.toneOfVoiceDescription")}
              </p>
            </div>
            <FormField
              control={form.control}
              name="toneOfVoice"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ""}
                      onFocus={() => setFocusedField("toneOfVoice")}
                      onChange={(event) => {
                        field.onChange(event)
                      }}
                      onBlur={(e) => {
                        setFocusedField(null)
                        onUpdate(e)
                      }}
                      placeholder={t("settings.company.customization.form.toneOfVoiceDescription")}
                      rows={8}
                      className="min-h-28 p-4 text-base md:text-base relative resize-none focus-visible:ring-0 focus-visible:border-0 rounded-none border-none ml-[2px] w-[calc(100%-4px)] outline-0 overflow-hidden"
                      maxLength={2048}
                      maxLengthClassName="p-4 text-muted-foreground/70"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Card>
          <Card
            className={cn(
              "flex flex-col",
              focusedField === "additionalInfo" && "border-ring ring-ring/50 ring-[3px]",
              issuesByField.additionalInfo?.length &&
                "border-destructive/50 ring-1 ring-destructive/15",
            )}
          >
            <div className="flex flex-col gap-0 p-4 bg-muted rounded-t-md">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">
                  {t("settings.company.customization.form.additionalInfo")}
                </h3>
                <div className="flex h-9 w-9 items-center justify-center">
                  {isSavingField === "additionalInfo" ? (
                    <LoaderCircle className="h-5 w-5 animate-spin" />
                  ) : (
                    <ComposerPromptValidationIssues
                      issues={issuesByField.additionalInfo ?? []}
                      getFieldText={(field) => getCustomizationFieldValue(form.getValues(), field)}
                      onApplySuggestion={applySuggestion}
                      onOpenChange={handleValidationReviewOpenChange}
                    />
                  )}
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {t("settings.company.customization.form.additionalInfoDescription")}
              </p>
            </div>
            <FormField
              control={form.control}
              name="additionalInfo"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ""}
                      onFocus={() => setFocusedField("additionalInfo")}
                      onChange={(event) => {
                        field.onChange(event)
                      }}
                      onBlur={(e) => {
                        setFocusedField(null)
                        onUpdate(e)
                      }}
                      placeholder={t(
                        "settings.company.customization.form.additionalInfoDescription",
                      )}
                      rows={8}
                      className="min-h-28 p-4 text-base md:text-base relative resize-none focus-visible:ring-0 focus-visible:border-0 rounded-none border-none ml-[2px] w-[calc(100%-4px)] outline-0 overflow-hidden"
                      maxLength={1024}
                      maxLengthClassName="p-4 text-muted-foreground/70"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Card>
        </div>
      </Form>
      {isHistoryDialogOpen && !!company?.id && (
        <ChangeLogItem
          open={isHistoryDialogOpen}
          onOpenChange={setIsHistoryDialogOpen}
          model="Company"
          recordId={company.id.toString()}
          title={company.name}
          fieldsMap={[
            { field: "description", label: t("settings.company.customization.form.description") },
            { field: "products", label: t("settings.company.customization.form.products") },
            { field: "toneOfVoice", label: t("settings.company.customization.form.toneOfVoice") },
            {
              field: "additionalInfo",
              label: t("settings.company.customization.form.additionalInfo"),
            },
          ]}
          canApplyChanges={(values) => {
            const currentValues = form.getValues()
            const snapshotKeys = Object.keys(values)
            return snapshotKeys.some((key) => {
              const typedKey = key as keyof typeof values
              return values[typedKey] !== currentValues[typedKey]
            })
          }}
          onApplyChanges={async (values) => {
            const snapshotKeys = Object.keys(values) as Array<keyof typeof values>
            snapshotKeys.forEach((key) => {
              form.setValue(key, values[key])
            })
            await onUpdate(undefined, undefined)
          }}
        />
      )}
    </div>
  )

  return (
    <div className="flex flex-col gap-4">
      {content}
      {company?.id ? (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="flex items-center justify-center w-fit gap-2 px-3"
            onClick={() => setIsHistoryDialogOpen(true)}
          >
            <span className="text-sm font-medium">{t("shared.changeHistory.title")}</span>
            <History className="h-4 w-4" />
          </Button>
        </div>
      ) : null}
    </div>
  )
}

const customizationFields = ["description", "products", "toneOfVoice", "additionalInfo"] as const

function isCustomizationField(field: string): field is (typeof customizationFields)[number] {
  return customizationFields.includes(field as (typeof customizationFields)[number])
}

function getCustomizationFieldValue(values: z.infer<typeof CompanySchema>, field: string) {
  return isCustomizationField(field) ? (values[field] ?? "") : ""
}
